import { createContext, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { REACT_QUERY_KEYS } from "../enums/react_query_key_enum";
import useBackend from "../hooks/use_backend";
import { useParams } from "react-router-dom";
import { useModal } from "./modal_provider";
import { fetchSchematicMetering } from "../scripts/api_v2/schematic_client";

const SchematicContext = createContext({});

export const SchematicProvider = ({ children }) => {
  const { overrideModalOpen } = useModal();
  const [selectedDevice, setSelectedDevice] = useState({});
  const [selectedNode, setSelectedNode] = useState({});
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [linkDeviceOpen, setLinkDeviceOpen] = useState(false);
  const [schematic, setSchematic] = useState({});
  const [showPhases, setShowPhases] = useState(false);
  const backend = useBackend();
  const params = useParams();

  let { data: schematicMetering, isLoading: isSchematicLoading } = useQuery({
    queryKey: [REACT_QUERY_KEYS.SCHEMATIC_METERING, backend.fetch, params.site_id, showPhases],
    queryFn: () => fetchSchematicMetering(backend.fetch, params.site_id, showPhases),
    refetchInterval: 5000,
    enabled: !overrideModalOpen,
  });

  function phasesAvailable(nodeId = 0) {
    if (!schematicMetering || !schematicMetering[nodeId] || !schematicMetering[nodeId].l1) return false;
    return true;
  }

  function nodeMetering(nodeId, field = null) {
    if (!schematicMetering || !schematicMetering[nodeId]) {
      return { value: null, l1: null, l2: null, l3: null };
    }
    const metering = schematicMetering[nodeId];
    if (!metering) return null;

    if (field === null) return metering;

    if (field === "other") {
      const cons = nodeMetering(nodeId, "consumption");
      const prod = nodeMetering(nodeId, "production");
      const um = nodeMetering(nodeId, "unmeasured");
      const curt = nodeMetering(nodeId, "curtailment");
      return {
        value: cons.value - prod.value + um.value + curt.value,
        l1: cons.l1 !== undefined ? cons.l1 - prod.l1 + um.l1 + curt.l1 : null,
        l2: cons.l2 !== undefined ? cons.l2 - prod.l2 + um.l2 + curt.l2 : null,
        l3: cons.l3 !== undefined ? cons.l3 - prod.l3 + um.l3 + curt.l3 : null,
      };
    }
    if (metering.l1 !== undefined && metering.l1[field] !== undefined) {
      return { value: metering[field], l1: metering.l1[field], l2: metering.l2[field], l3: metering.l3[field] };
    } else if (metering[field] !== undefined) {
      return { value: metering[field], l1: 0, l2: 0, l3: 0 };
    } else {
      return { value: 0, l1: 0, l2: 0, l3: 0 };
    }
  }

  return (
    <SchematicContext.Provider
      value={{
        selectedDevice,
        setSelectedDevice,
        deviceDialogOpen,
        setDeviceDialogOpen,
        editing,
        setEditing,
        schematic,
        setSchematic,
        linkDeviceOpen,
        setLinkDeviceOpen,
        selectedNode,
        setSelectedNode,
        schematicMetering,
        nodeMetering,
        showPhases,
        setShowPhases,
        phasesAvailable,
        isSchematicLoading,
      }}
    >
      {children}
    </SchematicContext.Provider>
  );
};

export default SchematicContext;
